import React, { createContext, useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { BookingDataResponse } from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import { PreviousBookingContextState } from './previousBookingContext.types';

const contextDefaultValues: PreviousBookingContextState = {
  previousBookingData: null,
  setPreviousBookingData: () => undefined,
  isInCooldownPeriod: true,
};

export const PreviousBookingContext =
  createContext<PreviousBookingContextState>(contextDefaultValues);

export const usePreviousBookingContext = (): PreviousBookingContextState =>
  useContext(PreviousBookingContext);

export const PreviousBookingContextProvider: React.FC<
  React.PropsWithChildren<object>
> = ({ children }) => {
  const [previousBookingData, setPreviousBookingData] =
    useState<BookingDataResponse | null>(null);

  const isInCooldownPeriod = useMemo(() => {
    // If no previous booking data, we consider the trip is not started yet
    if (!previousBookingData) {
      return true;
    }

    const { policyCooldownPeriod, bookingData } = previousBookingData;
    // allow 0 as valid value
    if (policyCooldownPeriod === undefined || policyCooldownPeriod === null) {
      throw new Error('Policy cooldown period is not set');
    }

    if (!bookingData || !bookingData.subscriptionDate) {
      throw new Error('Subscription date is not set');
    }

    const subscriptionDate = dayjs(bookingData.subscriptionDate).startOf('day');
    const today = dayjs().startOf('day');
    const daysSinceSubscription = today.diff(subscriptionDate, 'day');
    return daysSinceSubscription < policyCooldownPeriod;
  }, [previousBookingData]);

  const contextValue: PreviousBookingContextState = useMemo(
    () => ({
      previousBookingData,
      setPreviousBookingData,
      isInCooldownPeriod,
    }),
    [previousBookingData, isInCooldownPeriod],
  );

  return (
    <PreviousBookingContext.Provider value={contextValue}>
      {children}
    </PreviousBookingContext.Provider>
  );
};
